import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { 
  setMostFavourites, 
  setUserFavourites, 
  setLoading, 
  setError,
  addFavourite,
  removeFavourite,
  clearFavourites
} from '../features/favourites-slice';
import { useFavouriteApiService } from '../api/providers/AppServiceProvider';
import { CreateFavourite } from '../models/favourite';

export const useFavourites = () => {
  const dispatch = useAppDispatch();
  const favourites = useAppSelector((state) => state.favourites);
  const favouriteApiService = useFavouriteApiService();

  const loadMostFavourites = useCallback(async () => {
    // Only load if not already loaded
    if (favourites.hasLoadedMostFavourites) {
      return;
    }

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      const data = await favouriteApiService.getMostFavourites();
      
      dispatch(setMostFavourites(data));
    } catch (error) {
      console.error('Error loading most favourites:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Failed to load most favourites'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, favourites.hasLoadedMostFavourites]);

  const loadUserFavourites = useCallback(async () => {
    // Only load if not already loaded
    if (favourites.hasLoadedUserFavourites) {
      return;
    }

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      const data = await favouriteApiService.getUserFavourites();
      
      dispatch(setUserFavourites(data));
    } catch (error) {
      console.error('Error loading user favourites:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Failed to load user favourites'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, favourites.hasLoadedUserFavourites]);

  const createFavourite = useCallback(async (data: CreateFavourite) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      const newFavourite = await favouriteApiService.createFavourite(data);
      
      dispatch(addFavourite(newFavourite));
      return newFavourite;
    } catch (error) {
      console.error('Error creating favourite:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Failed to create favourite'));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const deleteFavourite = useCallback(async (id: number) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      await favouriteApiService.deleteFavourite(id);
      
      dispatch(removeFavourite(id));
    } catch (error) {
      console.error('Error deleting favourite:', error);
      dispatch(setError(error instanceof Error ? error.message : 'Failed to delete favourite'));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const clearAllFavourites = useCallback(() => {
    dispatch(clearFavourites());
  }, [dispatch]);

  return {
    mostFavourites: favourites.mostFavourites,
    userFavourites: favourites.userFavourites,
    isLoading: favourites.isLoading,
    error: favourites.error,
    hasLoadedMostFavourites: favourites.hasLoadedMostFavourites,
    hasLoadedUserFavourites: favourites.hasLoadedUserFavourites,
    loadMostFavourites,
    loadUserFavourites,
    createFavourite,
    deleteFavourite,
    clearAllFavourites,
  };
};
